import React, { useRef, useMemo } from "react";

import { Box, Typography } from "@mui/material";
import { useReportGeneratorContext } from "components/Report-generator/context/ReportGenerator-context";
import { getMacroBriefCapacity, MACRO_EDIT_CHROME_PX } from "components/Report-generator/utils/macroBriefLayout";
import type { CardConfig, MacroBriefAvailableIndicator, MacroBriefCardConfig } from "store/types";

import { CheckRow } from "./CheckRow";
import { SectionLabel } from "./SectionLabel";

interface Props {
  config: MacroBriefCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
  heightPx?: number;
}

export const MacroBriefSettings: React.FC<Props> = ({ config, onUpdateConfig, heightPx }) => {
  const { macroBriefIndicators } = useReportGeneratorContext();

  // Max indicators that fit into the card's section at its current height. The
  // narrative block (when shown) reserves room, so it lowers the capacity too.
  const maxIndicators = useMemo(
    () => getMacroBriefCapacity(heightPx, MACRO_EDIT_CHROME_PX, config.showNarrative),
    [heightPx, config.showNarrative]
  );
  const isAtCapacity = config.indicators.length >= maxIndicators;

  // Ref keeps the latest indicators in sync so rapid clicks don't read stale props
  const indicatorsRef = useRef(config.indicators);
  indicatorsRef.current = config.indicators;

  const grouped = useMemo(() => {
    const map = new Map<string, MacroBriefAvailableIndicator[]>();
    for (const ind of macroBriefIndicators) {
      const group = map.get(ind.category) || [];
      group.push(ind);
      map.set(ind.category, group);
    }
    return map;
  }, [macroBriefIndicators]);

  return (
    <Box>
      <Box sx={{ mb: 1.5 }}>
        <SectionLabel>Display</SectionLabel>
        <CheckRow
          label="Narrative"
          checked={config.showNarrative ?? false}
          onChange={(v) => onUpdateConfig({ ...config, showNarrative: v })}
        />
      </Box>
      {isAtCapacity && (
        <Typography sx={{ fontSize: 11, color: "#9CA3AF", mb: 1.5 }}>
          No more indicators fit in this card. Make it taller to add more.
        </Typography>
      )}
      {[...grouped.entries()].map(([category, items]) => (
        <Box key={category} sx={{ mb: 1.5 }}>
          <SectionLabel>{category}</SectionLabel>
          {items.map((ind) => {
            const checked = config.indicators.includes(ind.key);
            return (
              <CheckRow
                key={ind.key}
                label={ind.label}
                checked={checked}
                disabled={!checked && isAtCapacity}
                onChange={(v) => {
                  const current = indicatorsRef.current;
                  // Adding would overflow the section — ignore the click.
                  if (v && current.length >= maxIndicators) return;
                  const next: string[] = v
                    ? [...current, ind.key]
                    : current.filter((k) => k !== ind.key);
                  indicatorsRef.current = next;
                  onUpdateConfig({ ...config, indicators: next });
                }}
              />
            );
          })}
        </Box>
      ))}
      {macroBriefIndicators.length === 0 && (
        <Typography sx={{ fontSize: 12, color: "#9CA3AF", py: 2 }}>No indicators available</Typography>
      )}
    </Box>
  );
};
